import { useEffect, useRef, useCallback } from "react";
import * as THREE from "three";

const CITIES = [
  { lat: 31.7683, lng: 35.2137 },
  { lat: 41.3275, lng: 19.8187 },
  { lat: 40.7128, lng: -74.006 },
  { lat: 51.5074, lng: -0.1278 },
  { lat: 48.8566, lng: 2.3522 },
  { lat: 35.6762, lng: 139.6503 },
  { lat: 1.3521, lng: 103.8198 },
  { lat: 55.7558, lng: 37.6173 },
  { lat: -33.8688, lng: 151.2093 },
  { lat: 19.076, lng: 72.8777 },
];

const ARC_PAIRS = [
  [0, 1], [0, 3], [1, 4], [2, 3], [3, 5],
  [4, 7], [5, 8], [6, 9], [2, 6], [7, 9],
];

function latLngToVec3(lat, lng, r = 1) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -Math.sin(phi) * Math.cos(theta) * r,
    Math.cos(phi) * r,
    Math.sin(phi) * Math.sin(theta) * r
  );
}

function lerp(a, b, t) {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

export default function Globe({ scrollProgressRef }) {
  const mountRef = useRef(null);
  const cleanupRef = useRef(null);

  const initScene = useCallback(() => {
    const container = mountRef.current;
    if (!container) return;

    const W = container.clientWidth || 600;
    const H = container.clientHeight || 600;

    /* renderer */
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    /* scene / camera */
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 100);
    camera.position.z = 3.2;

    /* ── wireframe icosahedron (primary) ── */
    const icoGeo = new THREE.IcosahedronGeometry(1.2, 2);
    const wireframe = new THREE.LineSegments(
      new THREE.WireframeGeometry(icoGeo),
      new THREE.LineBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.35 })
    );
    scene.add(wireframe);

    /* inner solid sphere (subtle) */
    const innerGeo = new THREE.IcosahedronGeometry(1.18, 2);
    const innerMat = new THREE.MeshPhongMaterial({
      color: 0x020a1e, emissive: 0x030d2a, specular: 0x0044ff,
      shininess: 60, transparent: true, opacity: 0.5,
    });
    const innerMesh = new THREE.Mesh(innerGeo, innerMat);
    scene.add(innerMesh);

    /* ── vertex particles ── */
    const positions = icoGeo.attributes.position;
    const seen = new Set();
    const uniqueVerts = [];
    for (let i = 0; i < positions.count; i++) {
      const key = `${positions.getX(i).toFixed(3)},${positions.getY(i).toFixed(3)},${positions.getZ(i).toFixed(3)}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueVerts.push(new THREE.Vector3(positions.getX(i), positions.getY(i), positions.getZ(i)));
      }
    }
    const particleGeo = new THREE.BufferGeometry();
    const pPositions = new Float32Array(uniqueVerts.length * 3);
    uniqueVerts.forEach((v, i) => { pPositions[i * 3] = v.x; pPositions[i * 3 + 1] = v.y; pPositions[i * 3 + 2] = v.z; });
    particleGeo.setAttribute("position", new THREE.BufferAttribute(pPositions, 3));
    const particleMat = new THREE.PointsMaterial({ color: 0x00f0ff, size: 0.04, transparent: true, opacity: 0.8 });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    /* ── city dots on sphere ── */
    const cityGroup = new THREE.Group();
    CITIES.forEach(({ lat, lng }) => {
      const pos = latLngToVec3(lat, lng, 1.22);
      const dotG = new THREE.SphereGeometry(0.02, 8, 8);
      const dotM = new THREE.MeshBasicMaterial({ color: 0x00f0ff });
      const dot = new THREE.Mesh(dotG, dotM);
      dot.position.copy(pos);
      cityGroup.add(dot);

      const ringG = new THREE.RingGeometry(0.03, 0.042, 16);
      const ringM = new THREE.MeshBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
      const ring = new THREE.Mesh(ringG, ringM);
      ring.position.copy(pos);
      ring.lookAt(new THREE.Vector3(0, 0, 0));
      ring.userData.isRing = true;
      cityGroup.add(ring);
    });
    scene.add(cityGroup);

    /* ── arcs ── */
    const arcGroup = new THREE.Group();
    const arcDots = [];
    ARC_PAIRS.forEach(([i, j], idx) => {
      const a = CITIES[i], b = CITIES[j];
      const start = latLngToVec3(a.lat, a.lng, 1.22);
      const end = latLngToVec3(b.lat, b.lng, 1.22);
      const mid = start.clone().add(end).multiplyScalar(0.5);
      mid.normalize().multiplyScalar(1.65);
      const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
      const pts = curve.getPoints(50);
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.15 });
      arcGroup.add(new THREE.Line(geo, mat));
      const dG = new THREE.SphereGeometry(0.015, 6, 6);
      const dM = new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true });
      const dot = new THREE.Mesh(dG, dM);
      dot.userData.curve = curve;
      dot.userData.progress = idx / ARC_PAIRS.length;
      arcDots.push(dot);
      arcGroup.add(dot);
    });
    scene.add(arcGroup);

    /* ── outer glow shells ── */
    const glowMesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.35, 2),
      new THREE.MeshBasicMaterial({ color: 0x0044ff, transparent: true, opacity: 0.06, side: THREE.BackSide })
    );
    scene.add(glowMesh);
    const glow2 = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.5, 2),
      new THREE.MeshBasicMaterial({ color: 0x0088ff, transparent: true, opacity: 0.03, side: THREE.BackSide })
    );
    scene.add(glow2);

    /* ── lights ── */
    scene.add(new THREE.AmbientLight(0x001133, 2));
    const pLight = new THREE.PointLight(0x0088ff, 3, 10);
    pLight.position.set(3, 3, 3);
    scene.add(pLight);
    const pLight2 = new THREE.PointLight(0x00f0ff, 1.5, 10);
    pLight2.position.set(-2, -1, 2);
    scene.add(pLight2);

    /* ── animation state ── */
    let ringPulse = 0;
    const clock = new THREE.Clock();

    /* ── animation loop ── */
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const sp = scrollProgressRef?.current ?? 0;

      /* === SCROLL-DRIVEN PHASES === */
      // Phase 1 (0-0.15): Normal auto-rotate
      // Phase 2 (0.15-0.35): Scale up + glow intensifies
      // Phase 3 (0.35-0.55): Morph - wireframe pulses, particles expand
      // Phase 4 (0.55-0.8): Shrink to bg, content appears
      // Phase 5 (0.8-1.0): Subtle bg element

      /* scale */
      let targetScale;
      if (sp < 0.15) targetScale = 1;
      else if (sp < 0.35) targetScale = lerp(1, 1.6, (sp - 0.15) / 0.2);
      else if (sp < 0.55) targetScale = lerp(1.6, 1.4, (sp - 0.35) / 0.2);
      else if (sp < 0.8) targetScale = lerp(1.4, 0.6, (sp - 0.55) / 0.25);
      else targetScale = lerp(0.6, 0.5, (sp - 0.8) / 0.2);

      const s = targetScale;
      wireframe.scale.setScalar(s);
      innerMesh.scale.setScalar(s);
      particles.scale.setScalar(s);
      cityGroup.scale.setScalar(s);
      arcGroup.scale.setScalar(s);
      glowMesh.scale.setScalar(s * 1.12);
      glow2.scale.setScalar(s * 1.25);

      /* rotation speed based on phase */
      const baseSpeed = sp < 0.35 ? 0.3 : sp < 0.55 ? 0.6 : 0.15;
      const rotY = t * baseSpeed;
      const rotX = Math.sin(t * 0.15) * 0.15;

      wireframe.rotation.set(rotX, rotY, 0);
      innerMesh.rotation.set(rotX, rotY, 0);
      particles.rotation.set(rotX, rotY, 0);
      cityGroup.rotation.set(rotX, rotY, 0);
      arcGroup.rotation.set(rotX, rotY, 0);

      /* wireframe opacity / glow */
      const wireOpacity = sp < 0.15 ? 0.35
        : sp < 0.35 ? lerp(0.35, 0.7, (sp - 0.15) / 0.2)
        : sp < 0.55 ? lerp(0.7, 0.5, (sp - 0.35) / 0.2)
        : lerp(0.5, 0.15, (sp - 0.55) / 0.45);
      wireframe.material.opacity = wireOpacity;

      /* glow intensity */
      const glowOp = sp < 0.15 ? 0.06
        : sp < 0.55 ? lerp(0.06, 0.2, (sp - 0.15) / 0.4)
        : lerp(0.2, 0.04, (sp - 0.55) / 0.45);
      glowMesh.material.opacity = glowOp;
      glow2.material.opacity = glowOp * 0.5;

      /* particle scatter in phase 3 */
      if (sp > 0.3 && sp < 0.6) {
        const scatter = Math.sin((sp - 0.3) / 0.3 * Math.PI) * 0.15;
        const posAttr = particles.geometry.attributes.position;
        for (let i = 0; i < uniqueVerts.length; i++) {
          const v = uniqueVerts[i];
          const n = v.clone().normalize();
          posAttr.setXYZ(i, v.x + n.x * scatter * Math.sin(t * 2 + i), v.y + n.y * scatter * Math.cos(t * 2 + i), v.z + n.z * scatter * Math.sin(t * 3 + i * 0.5));
        }
        posAttr.needsUpdate = true;
      }

      /* particle opacity */
      particleMat.opacity = sp < 0.55 ? 0.8 : lerp(0.8, 0.3, (sp - 0.55) / 0.45);

      /* ring pulse */
      ringPulse = (ringPulse + 0.02) % 1;
      cityGroup.children.forEach((c) => {
        if (c.userData.isRing) {
          const p = (ringPulse + c.id * 0.07) % 1;
          c.material.opacity = (1 - p) * 0.6;
          c.scale.setScalar(1 + p * 1.2);
        }
      });

      /* arc dots */
      arcDots.forEach((dot) => {
        dot.userData.progress = (dot.userData.progress + 0.003) % 1;
        const pos = dot.userData.curve.getPoint(dot.userData.progress);
        dot.position.copy(pos);
        dot.material.opacity = Math.sin(dot.userData.progress * Math.PI);
      });

      /* camera z based on scroll */
      camera.position.z = sp < 0.15 ? 3.2
        : sp < 0.35 ? lerp(3.2, 2.2, (sp - 0.15) / 0.2)
        : sp < 0.55 ? lerp(2.2, 2.5, (sp - 0.35) / 0.2)
        : lerp(2.5, 4, (sp - 0.55) / 0.45);

      /* light orbit */
      pLight.position.x = Math.sin(t * 0.4) * 3;
      pLight.position.z = Math.cos(t * 0.4) * 3;

      renderer.render(scene, camera);
    };
    animate();

    /* resize */
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    cleanupRef.current = () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [scrollProgressRef]);

  useEffect(() => {
    initScene();
    return () => { if (cleanupRef.current) cleanupRef.current(); };
  }, [initScene]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div style={{
        position: "absolute", inset: "-20%", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(0,80,255,0.3) 0%, rgba(0,200,255,0.12) 45%, transparent 70%)",
        filter: "blur(40px)", pointerEvents: "none", animation: "glow-pulse 3s ease-in-out infinite",
      }} />
      <div ref={mountRef} style={{ width: "100%", height: "100%", cursor: "default", position: "relative", zIndex: 1 }} />
    </div>
  );
}
